<script>
    /**
     * "Deadlines" tab: every supplier cancellation deadline of the quotation, soonest first,
     * with a one-line verdict on top and a colour legend below.
     */
    import { t, locale, formatDate } from '../../../../i18n';
    import { collectDeadlines, countByLevel, LEVEL_STYLE } from '../../../../class/business/Deadlines';
    import DeadlineBadge from '../../DeadlineBadge.svelte';

    export let data = {};

    $: list   = collectDeadlines(data);
    $: counts = countByLevel(list);
    $: critical = counts.critical || 0;
    $: warning  = counts.warning  || 0;
</script>

<style>
    .deadlines-alert { display: flex; align-items: center; gap: 0.6rem; font-weight: 500; }
    .deadlines-alert i { font-size: 1.4rem; }
    .deadlines-legend { display: flex; flex-wrap: wrap; gap: 0.4rem 1.2rem; font-size: 0.85rem; color: #828D99; margin-top: 1rem; }
    .deadlines-legend span { display: inline-flex; align-items: center; gap: 0.35rem; }
    .deadlines-legend .swatch { display: inline-block; width: 0.8rem; height: 0.8rem; border-radius: 50%; }
    td.deadline-name { font-weight: 500; }
</style>

<p class="text-muted mb-1">{$t('deadlines.intro')}</p>

{#if critical}
    <div class="alert alert-danger deadlines-alert" role="alert">
        <i class="bx bxs-error"></i>
        <span>{$t('deadlines.summary_critical', { n: critical })}{#if warning}&nbsp;·&nbsp;{$t('deadlines.summary_warning', { n: warning })}{/if}</span>
    </div>
{:else if warning}
    <div class="alert alert-warning deadlines-alert" role="alert">
        <i class="bx bx-time-five"></i>
        <span>{$t('deadlines.summary_warning', { n: warning })}</span>
    </div>
{:else}
    <div class="alert alert-success deadlines-alert" role="alert">
        <i class="bx bx-calendar-check"></i>
        <span>{$t('deadlines.none_soon')}</span>
    </div>
{/if}

<div class="table-responsive">
    <table class="table mb-0">
        <thead class="thead-dark">
        <tr>
            <th>{$t('deadlines.supplier')}</th>
            <th>{$t('deadlines.type')}</th>
            <th>{$t('deadlines.city')}</th>
            <th>{$t('deadlines.day')}</th>
            <th>{$t('deadlines.date')}</th>
            <th>{$t('deadlines.time_left')}</th>
            <th>{$t('deadlines.since')}</th>
        </tr>
        </thead>
        <tbody>
        {#each list as d}
            <tr>
                <td class="deadline-name">{d.name}</td>
                <td>{$t('deadlines.type_' + d.type)}</td>
                <td>{d.city}</td>
                <td>{d.day ? $t('common.day_n', { n: d.day }) : ''}</td>
                <td>{formatDate(d.end, $locale)}</td>
                <td><DeadlineBadge deadline={d.row.deadline}></DeadlineBadge></td>
                <td class="text-muted">{d.start ? formatDate(d.start, $locale) : '–'}</td>
            </tr>
        {/each}
        </tbody>
    </table>
</div>

<div class="deadlines-legend" aria-hidden="true">
    <span><i class="swatch {LEVEL_STYLE.critical.badge}"></i>{$t('deadlines.legend_critical')}</span>
    <span><i class="swatch {LEVEL_STYLE.warning.badge}"></i>{$t('deadlines.legend_warning')}</span>
    <span><i class="swatch {LEVEL_STYLE.moderate.badge}"></i>{$t('deadlines.legend_moderate')}</span>
    <span><i class="swatch {LEVEL_STYLE.safe.badge}"></i>{$t('deadlines.legend_safe')}</span>
    <span><i class="swatch {LEVEL_STYLE.passed.badge}"></i>{$t('deadlines.legend_passed')}</span>
</div>
